import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { restoreSavedCache, startSavingCache } from '@/lib/persist.ts';
import { NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useMe } from '@/lib/queries.ts';
import { realtime } from '@/lib/ws.ts';
import { bindRealtime, bindPlayerSubscription } from '@/stores/nowPlaying.ts';
import { bindProgressReporting } from '@/lib/progress.ts';
import { bindRecents } from '@/stores/recents.ts';
import { useUi } from '@/stores/ui.ts';
import { useItemMenu } from '@/stores/itemMenu.ts';
import { useBackdrop } from '@/stores/backdrop.ts';
import { usePlayer } from '@/stores/playback.ts';
import { exitFullscreen, onFullscreenChange } from '@/lib/fullscreen.ts';
import { armChrome, isChromeHidden, subscribeChrome, wakeChrome } from '@/lib/chrome.ts';
import { onKeyActivity } from '@/lib/keys.ts';
import { usePlayerKeys } from './usePlayerKeys.ts';
import { PlexLogin } from '@/features/auth/PlexLogin.tsx';
import { AccountMenu } from '@/features/auth/AccountMenu.tsx';
import { UpdateNotice } from '@/features/auth/UpdateNotice.tsx';
import { LiveTvPage } from '@/features/live/LiveTvPage.tsx';
import { AdminPage } from '@/features/admin/AdminPage.tsx';
import { RequestPage } from '@/features/search/RequestPage.tsx';
import { useLiveView } from '@/stores/viewMode.ts';
import { LibraryBar } from '@/features/library/LibraryBar.tsx';
import { useFocusMemory } from '@/ui/useFocusMemory.ts';
import {
  IconLibrary, IconLiveTv, IconRadio, IconAccounts, IconPlaying, IconGames,
} from '@/ui/icons.tsx';
import { GamesPage } from '@/features/games/GamesPage.tsx';
import { SearchPage } from '@/features/search/SearchPage.tsx';
import { ItemMenu } from '@/features/library/ItemMenu.tsx';
import { useLibraryItem } from '@/lib/queries.ts';
import { pushKeyLayer, type NavKey } from '@/lib/keys.ts';
import { NavSearch } from '@/features/search/NavSearch.tsx';
import { LibraryPage } from '@/features/library/LibraryPage.tsx';
import { SectionPage } from '@/features/library/SectionPage.tsx';
import { ItemPage } from '@/features/library/ItemPage.tsx';
import { ChannelGrid } from '@/features/channels/ChannelGrid.tsx';
import { PlayerHost } from './PlayerHost.tsx';
import { MiniPlayer } from './MiniPlayer.tsx';
import { Backdrop } from './Backdrop.tsx';
import { RecentStrip } from './RecentStrip.tsx';
import { TuningCard } from './TuningCard.tsx';
import { FocusProvider, Focusable, navProps } from '@/ui/Focusable.tsx';

export function App() {
  const { data, isLoading, error } = useMe();

  /*
   * Where the cursor was, per screen — see lib/focus-memory.ts. Here rather
   * than in each page because every screen has the same problem, and the two
   * that forgot to opt in would be the two somebody complains about.
   */
  useFocusMemory();

  // Whether the "Now playing" door belongs in the navigation at all.
  const { channel: onNow, item: itemNow } = usePlayer();
  const playing = !!onNow || !!itemNow;
  const signedIn = !!data?.user && !error;

  /*
   * LAST TIME'S SCREENS, BEFORE ANYTHING RENDERS. Put back during render, the
   * moment we know who this is and before a single row mounts, so the first
   * frame of the library is the library rather than a spinner. Synchronous
   * and once per person; see lib/persist.ts for what is kept and why.
   */
  const qc = useQueryClient();
  const uid = signedIn ? data!.user.id : null;
  const restoredFor = useRef<string | null>(null);
  if (uid && restoredFor.current !== uid) {
    restoredFor.current = uid;
    restoreSavedCache(qc, uid);
  }
  useEffect(() => (uid ? startSavingCache(qc, uid) : undefined), [qc, uid]);

  const { view, setView } = useUi();
  /*
   * Some screens own the whole viewport rather than scrolling inside it, and
   * the shell is told which by a class.
   *
   * Derived from the VIEW and not only the path, because the guide stopped
   * having a path of its own when it merged into Live TV. `.shell.route-guide`
   * is what gives the guide the full viewport and reserves the well the
   * preview picture sits in — keying it off the URL alone would have silently
   * dropped all of that the moment the route went away, and the symptom would
   * have been a guide laid out like a scrolling page with the preview
   * somewhere else entirely.
   */
  const location = useLocation();
  const path = location.pathname.split('/')[1] || 'library';
  const liveView = useLiveView((s) => s.view);
  const route = path === 'live' && liveView === 'guide' ? 'guide' : path;
  const { channel, item } = usePlayer();
  /*
   * "Is there a picture to present?" — which is NOT "is a TV channel tuned".
   *
   * This read `channel?.kind === 'tv'` and nothing else, so a library item —
   * which sets `item` and leaves `channel` null — was invisible to it. The
   * effect below then forced the view straight back out of theater in the
   * same tick the library asked for it, and the symptom was a film that
   * simply never appeared. Radio is the reason the test exists at all: audio
   * has no picture and must not black out the screen.
   */
  const hasVideo = channel?.kind === 'tv' || !!item;
  const shellRef = useRef<HTMLDivElement>(null);

  const immersive = view !== 'browse';

  // The chrome's hidden/shown state is owned imperatively (lib/chrome.ts) so
  // a key handler can ask what it was AT THE MOMENT the key arrived, not one
  // render later. React only reads it.
  const idle = useSyncExternalStore(subscribeChrome, isChromeHidden, isChromeHidden);
  useEffect(() => { armChrome(immersive); }, [immersive]);

  // Mouse and touch still count as being here. Keys come through the key
  // pipeline instead, because the TV wrapper produces no DOM keydown at all.
  useEffect(() => {
    const poke = () => { wakeChrome(); };
    const events = ['mousemove', 'mousedown', 'touchstart', 'wheel'] as const;
    for (const e of events) window.addEventListener(e, poke, { passive: true });
    const offKeys = onKeyActivity(poke);
    return () => {
      for (const e of events) window.removeEventListener(e, poke);
      offKeys();
    };
  }, []);

  /*
   * THE ROOM IS A LIBRARY IDEA, AND IT DOES NOT FOLLOW YOU OUT.
   *
   * The backdrop is set by whatever the cursor lands on and was cleared by
   * nothing, so walking from a film into Live TV took the film with it — and
   * a photograph under a grid of programme names makes them genuinely hard
   * to read. Clearing on every route change is the whole fix, and it needs no
   * list of which screens want a picture: anything that does sets one within
   * a frame or two of arriving, and the backdrop's own settle delay means the
   * clear never flashes on the way.
   *
   * It is also what the library home wanted — you arrive to the house mark
   * rather than to whatever you were looking at last time, until the cursor
   * touches a title.
   */
  useEffect(() => { useBackdrop.getState().set(null); }, [location.pathname]);

  /*
   * MENU ON A POSTER, FROM ONE PLACE.
   *
   * Posters appear on the library home, in every section, in search results
   * and on a detail page. Wiring a context menu into each is four copies of
   * the same thing and, reliably, three of them — the fourth gets added when
   * somebody notices the key does nothing on that one screen.
   *
   * So it reads the DOM instead: whatever holds the cursor declares what it
   * is with `data-nav`, and a poster's is `lib-<ratingKey>`. Same reasoning as
   * the ambient backdrop — the element already says what it is, so nothing
   * has to be registered anywhere.
   */
  const menuItemId = useItemMenu((s) => s.id);
  useEffect(() => pushKeyLayer((key: NavKey) => {
    if (key !== 'menu') return false;
    const id = (document.activeElement as HTMLElement | null)
      ?.getAttribute?.('data-nav')?.match(/^lib-(.+)$/)?.[1];
    if (!id) return false;      // not on a poster: somebody else's key
    useItemMenu.getState().open(id);
    return true;
  }), []);

  usePlayerKeys(immersive);

  useEffect(() => {
    if (!signedIn) return;
    const unbind = bindRealtime();
    const unbindPlayer = bindPlayerSubscription();
    const unbindRecents = bindRecents();
    // Bound here rather than in the library screen: a component owning this
    // would stop reporting the moment somebody navigated away from the screen
    // they started playback on, which is most of the time.
    const unbindProgress = bindProgressReporting();
    realtime.connect();
    return () => { unbind(); unbindPlayer(); unbindRecents(); unbindProgress(); };
  }, [signedIn]);

  useEffect(() => {
    useUi.getState().setShell(shellRef.current);
  }, [signedIn]);

  // Anything below `fullscreen` must not still be in real fullscreen.
  useEffect(() => {
    if (view !== 'fullscreen') void exitFullscreen();
  }, [view]);

  // Fullscreen can end without us — browser Escape, F11, losing focus. When
  // it does, drop to theater rather than all the way out, which is what the
  // user asked for by pressing Escape once.
  useEffect(() => onFullscreenChange((active) => {
    if (!active && useUi.getState().view === 'fullscreen') setView('theater');
  }), [setView]);

  // Nothing playing means nothing to be immersive about.
  useEffect(() => {
    if (!hasVideo && view !== 'browse') setView('browse');
  }, [hasVideo, view, setView]);

  if (isLoading) return <div className="center muted">Loading…</div>;
  if (!signedIn) {
    return (
      <FocusProvider>
        <PlayerHost />
        <PlexLogin />
      </FocusProvider>
    );
  }

  return (
    <FocusProvider>
      <div
        ref={shellRef}
        className={[
          'shell',
          `view-${view}`,
          `route-${route}`,
          hasVideo ? 'has-video' : '',
          immersive && idle ? 'idle' : '',
        ].filter(Boolean).join(' ')}
      >
        {/* Above the router, outside every route: the media elements survive
            navigation. Classes change PRESENTATION only. */}
        <PlayerHost />

        {/* Behind everything, following the cursor. */}
        <Backdrop />

        <TuningCard />
        <RecentStrip />

        <div className="app">
          {/* These are ordinary anchors, and for a while that made them
              unreachable by remote: the old focus system only knew about
              elements routed through <Focusable>. Anything navigable now says
              so with data-nav, whatever rendered it. */}
          <nav className="nav" data-nav-zone="top">
            <Brand />
            {/* Ordered by how often they are used, left to right, because on
                a remote the leftmost item is the one you land on and every
                step right is a press. Radio is last for that reason. */}
            {/* The label is wrapped so a phone can hide it and keep it: at
                390px the bar is icons, but the word is still what a screen
                reader announces. */}
            <NavLink to="/library" {...navProps('nav-library')}>
              <IconLibrary size={18} /><span className="nav-label">Library</span>
            </NavLink>
            {/*
              A source somebody cannot reach is not shown greyed out or
              locked — their app is simply smaller, which is how Plex behaves
              with an unshared library. The server refuses regardless; this is
              only so nobody stares at a tab that will not work.
            */}
            {data?.user.can.liveTv && (
              <NavLink to="/live" {...navProps('nav-live')}>
                <IconLiveTv size={18} /><span className="nav-label">Live TV</span>
              </NavLink>
            )}
            {data?.user.can.radio && (
              <NavLink to="/radio" {...navProps('nav-radio')}>
                <IconRadio size={18} /><span className="nav-label">Radio</span>
              </NavLink>
            )}
            {/* After Radio: used less often than either, and the order of
                this bar is the order of how often things are used. */}
            {data?.user.can.games && (
              <NavLink to="/games" {...navProps('nav-games')}>
                <IconGames size={18} /><span className="nav-label">Games</span>
              </NavLink>
            )}
            {/*
              Admin, and not on a television.
              `html.tv` is set by the APK's own bootstrap, so this is absent in
              the living room by construction rather than by a check somebody
              could forget. Deciding who reaches the IPTV subscription is a
              desk job, and a remote is the wrong input device for it.
            */}
            {data?.user.can.admin && !document.documentElement.classList.contains('tv') && (
              <NavLink to="/admin" {...navProps('nav-admin')}>
                <IconAccounts size={18} /><span className="nav-label">Admin</span>
              </NavLink>
            )}
            {/*
              A WAY BACK TO WHAT IS PLAYING, from the one band of the screen
              that is always reachable.
              
              The now-playing bar lives in the bottom chrome, and moving DOWN
              walks through the content before it will leave for that band —
              which is right, except that the library scrolls forever. There
              is always another row below, so "walk to the bottom" never
              arrives and the bar was reachable only by accident. Somebody who
              backed out of a film could not pause it, stop it, or return to
              it; the only exits were starting something else or killing the
              app.
              
              So it gets a door in the navigation, which is one press from
              anywhere. It appears only while something is on.
            */}
            {playing && (
              <Focusable
                id="nav-playing"
                className="nav-playing"
                onSelect={() => setView('theater')}
              >
                <IconPlaying size={18} />
                <span className="nav-label">Now playing</span>
              </Focusable>
            )}

            {/* Pushed to the right by its own margin, so the three
                destinations keep the order they were given — Library, Live
                TV, Radio, by how often each is used. Search is not a
                destination; it is how you skip walking to one. */}
            <NavSearch />
            {/* Buffer and theme moved into the account dialog. They were two
                more focus stops between the tabs and the content on every
                screen, for settings changed about twice a year. */}
            <AccountMenu />
          </nav>

          {/* Beside the nav rather than inside a page, and renders nothing off
              the library routes. See LibraryBar — both facts are load-bearing
              for how focus behaves. */}
          <LibraryBar />

          {/* Renders nothing in a browser, and nothing when this television is
              already on the newest build. */}
          <UpdateNotice />

          <main className="main">
            {/*
              A CROSSFADE ON ARRIVAL, keyed by PATH and not by search.
              
              Routes swapped instantly, which is the single loudest "this was
              assembled" signal an app gives off — nothing in the physical
              world replaces itself between two frames.
              
              Keyed on pathname alone deliberately: keying on the full
              location would remount the search page on every keystroke,
              throwing away the text box and the results with it.
            */}
            <div className="page" key={location.pathname}>
            <Routes>
              <Route path="/radio" element={<ChannelGrid kind="radio" />} />
              <Route path="/live" element={<LiveTvPage />} />
              {/* The old address, kept so a bookmark or a re-tune that still
                  points at it lands somewhere sensible rather than at the
                  radio. */}
              <Route path="/guide" element={<Navigate to="/live" replace />} />
              <Route path="/search" element={<SearchPage />} />
              {/* The shelf. Playing a game leaves the app for
                  /games/player.html — a page of its own, see player.js. */}
              <Route path="/games" element={<GamesPage />} />
              {/* Something the house does not have yet. A screen rather than
                  a dialog — see RequestPage for why. */}
              <Route path="/request/:mediaType/:tmdbId" element={<RequestPage />} />
              <Route path="/library" element={<LibraryPage />} />
              <Route path="/library/s/:key" element={<SectionPage />} />
              <Route path="/library/item/:id" element={<ItemPage />} />
              {/* Browser only by construction: nothing links here from the TV
                  app, and the server refuses a non-admin whatever asks. */}
              {/* A SPLAT, because the admin screens are routes of their own:
                  the back button works between them, a half-finished import
                  is a link, and a reload lands where you were. */}
              <Route path="/admin/*" element={<AdminPage />} />
              {/* The library is where the app opens. Navigation is ordered by
                  how often each is used, left to right — on a remote the
                  leftmost is where you land and every step right is a press —
                  and the landing screen has to agree with that order or the
                  two say opposite things about what this app is for. */}
              <Route path="*" element={<Navigate to="/library" replace />} />
            </Routes>
            </div>
          </main>

          <MiniPlayer />

          {menuItemId && (
            <ItemMenuFor id={menuItemId} onClose={() => useItemMenu.getState().close()} />
          )}
        </div>
      </div>
    </FocusProvider>
  );
}

/**
 * ONE-TIME CACHE BREAK, and it should never need a second value.
 *
 * Files copied out of public/ keep the name they were given, so the artwork
 * changes but its URL does not. For a while the server was handing those out
 * as `immutable` for a year — a promise that the URL will never mean anything
 * else — which is true of a bundler's hashed assets and false of these. Every
 * device that fetched the logo in that window holds a copy it will never
 * revalidate: the televisions went on painting a logo that had been replaced
 * twice, and no amount of rebuilding could reach them.
 *
 * A query string is a different cache key, so this asks for something those
 * devices have never seen. The header is fixed at the source (see
 * cacheControl in http/server.ts) and the artwork now revalidates normally,
 * so bumping this again should not be necessary — if it ever seems to be,
 * the header is wrong again and THAT is the thing to fix.
 */
const BRAND_V = 2;

/**
 * The wordmark, with the typed name as its fallback.
 *
 * An <img> that 404s draws a broken-image glyph in the corner of every
 * screen, which is worse than no logo at all — so a failed load falls back to
 * the word, and an install that has not been given the artwork looks
 * deliberate rather than broken.
 */
function Brand() {
  const [ok, setOk] = useState(true);
  return (
    <div className="brand">
      {ok
        ? <img src={`/brand-wordmark.png?v=${BRAND_V}`} alt="N8Media" onError={() => setOk(false)} />
        : 'N8Media'}
    </div>
  );
}

/**
 * The menu needs the whole item; the cursor only gave us a rating key.
 *
 * A separate component so the query lives BELOW the shell — asking for it in
 * App would re-render the entire app every time a menu opened or closed, and
 * the shell contains the video element.
 */
function ItemMenuFor({ id, onClose }: { id: string; onClose(): void }) {
  const { data } = useLibraryItem(id);
  if (!data) return null;
  return <ItemMenu item={data} onClose={onClose} />;
}
